import type { BooleanInput, NumberInput } from '@angular/cdk/coercion';
import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, model, numberAttribute, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideChevronRight } from '@ng-icons/lucide';
import {
    BrnCalendar,
    BrnCalendarCell,
    BrnCalendarCellButton,
    BrnCalendarGrid,
    BrnCalendarHeader,
    BrnCalendarMonthSelect,
    BrnCalendarNextButton,
    BrnCalendarPreviousButton,
    BrnCalendarWeek,
    BrnCalendarWeekday,
    BrnCalendarYearSelect,
    injectBrnCalendarI18n,
    type Weekday,
} from '@spartan-ng/brain/calendar';
import { injectDateAdapter } from '@spartan-ng/brain/date-time';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { buttonVariants } from '@spartan-ng/helm/button';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { hlm } from '@spartan-ng/helm/utils';
import type { ClassValue } from 'clsx';

@Component({
    selector: 'hlm-calendar',
    imports: [
        BrnCalendar,
        BrnCalendarHeader,
        BrnCalendarNextButton,
        BrnCalendarPreviousButton,
        BrnCalendarWeekday,
        BrnCalendarWeek,
        BrnCalendarCellButton,
        BrnCalendarCell,
        BrnCalendarGrid,
        NgIcon,
        HlmIcon,
        BrnSelectImports,
        HlmSelectImports,
        BrnCalendarPreviousButton,
        BrnCalendarMonthSelect,
        BrnCalendarMonthSelect,
        BrnCalendarMonthSelect,
        BrnCalendarYearSelect,
        NgTemplateOutlet,
    ],
    viewProviders: [provideIcons({ lucideChevronLeft, lucideChevronRight })],
    styles: [
        `
            /* Override cell size based on cellSize input */
            :host ::ng-deep td[brnCalendarCell] {
                width: var(--cell-size, 42px) !important;
                height: var(--cell-size, 42px) !important;
            }

            /* Ensure calendar buttons fill the full cell */
            :host ::ng-deep button[brnCalendarCellButton] {
                width: var(--cell-size, 42px) !important;
                height: var(--cell-size, 42px) !important;
                border-radius: 6px !important;
            }

            /* Style available date buttons */
            :host ::ng-deep button[brnCalendarCellButton]:not([data-disabled]) {
                background-color: var(--color-primary-light) !important;
                color: #000 !important;
            }

            /* Style available date buttons on hover */
            :host ::ng-deep button[brnCalendarCellButton]:not([data-disabled]):hover {
                background-color: var(--color-primary-base) !important;
                color: #fff !important;
            }

            /* Style selected date button - make it fully round */
            :host ::ng-deep button[brnCalendarCellButton][data-selected] {
                background-color: var(--color-primary-base) !important;
                color: #fff !important;
                border-radius: 50% !important;
            }
        `,
    ],
    template: `
        <div
            brnCalendar
            [min]="min()"
            [max]="max()"
            [disabled]="disabled()"
            [(date)]="date"
            [dateDisabled]="dateDisabled()"
            [weekStartsOn]="weekStartsOn()"
            [defaultFocusedDate]="defaultFocusedDate()"
            [class]="_computedCalenderClass()"
        >
            <div class="inline-flex flex-col space-y-4">
                <!-- Header -->
                <div class="space-y-4">
                    <div class="relative flex items-center pt-1">
                        <div class="flex items-center gap-2">
                            <ng-template #month>
                                <brn-select brnCalendarMonthSelect>
                                    <hlm-select-trigger size="sm" [class]="_selectClass">
                                        <brn-select-value />
                                    </hlm-select-trigger>
                                    <hlm-select-content class="max-h-80">
                                        @for (month of _i18n.config().months(); track month) {
                                            <hlm-option [value]="month">{{ month }}</hlm-option>
                                        }
                                    </hlm-select-content>
                                </brn-select>
                            </ng-template>
                            <ng-template #year>
                                <brn-select brnCalendarYearSelect>
                                    <hlm-select-trigger size="sm" [class]="_selectClass">
                                        <brn-select-value />
                                    </hlm-select-trigger>
                                    <hlm-select-content class="max-h-80">
                                        @for (year of _i18n.config().years(); track year) {
                                            <hlm-option [value]="year">{{ year }}</hlm-option>
                                        }
                                    </hlm-select-content>
                                </brn-select>
                            </ng-template>
                            @let heading = _heading();
                            @switch (captionLayout()) {
                                @case ('dropdown') {
                                    <ng-container [ngTemplateOutlet]="month" />
                                    <ng-container [ngTemplateOutlet]="year" />
                                }
                                @case ('dropdown-months') {
                                    <ng-container [ngTemplateOutlet]="month" />
                                    <div brnCalendarHeader class="text-sm font-medium">{{ heading.year }}</div>
                                }
                                @case ('dropdown-years') {
                                    <div brnCalendarHeader class="text-sm font-medium">{{ heading.month }}</div>
                                    <ng-container [ngTemplateOutlet]="year" />
                                }
                                @case ('label') {
                                    <div brnCalendarHeader class="text-lg font-bold whitespace-nowrap">{{ _formatMonthYear() }}</div>
                                }
                            }

                            <button
                                brnCalendarPreviousButton
                                [disabled]="_isPreviousDisabled()"
                                class="focus-visible:ring-ring hover:bg-accent hover:text-accent-foreground text-popover-foreground inline-flex size-6 items-center justify-center whitespace-nowrap rounded-md bg-transparent p-0 text-sm font-medium transition-colors hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                            >
                                <ng-icon hlm name="lucideChevronLeft" size="base" />
                            </button>

                            <button
                                brnCalendarNextButton
                                [disabled]="_isNextDisabled()"
                                class="focus-visible:ring-ring hover:bg-accent hover:text-accent-foreground text-popover-foreground inline-flex size-6 items-center justify-center whitespace-nowrap rounded-md bg-transparent p-0 text-sm font-medium transition-colors hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                            >
                                <ng-icon hlm name="lucideChevronRight" size="base" />
                            </button>
                        </div>
                    </div>
                </div>

                <table class="w-full border-collapse space-y-1" brnCalendarGrid>
                    <thead>
                        <tr class="flex">
                            <th
                                *brnCalendarWeekday="let weekday"
                                scope="col"
                                class="text-muted-foreground w-8 rounded-md text-[0.8rem] font-normal"
                                [attr.aria-label]="_i18n.config().labelWeekday(weekday)"
                            >
                                {{ _formatWeekday(weekday) }}
                            </th>
                        </tr>
                    </thead>

                    <tbody role="rowgroup">
                        <tr *brnCalendarWeek="let week" class="mt-2 flex w-full">
                            @for (date of week; track _dateAdapter.getTime(date)) {
                                <td
                                    brnCalendarCell
                                    class="data-[selected]:data-[outside]:bg-accent/50 data-[selected]:bg-accent relative size-8 p-0 text-center text-sm focus-within:relative focus-within:z-20 first:data-[selected]:rounded-l-md last:data-[selected]:rounded-r-md [&:has([aria-selected].day-range-end)]:rounded-r-md"
                                >
                                    <button brnCalendarCellButton [date]="date" [class]="_btnClass">
                                        {{ _dateAdapter.getDate(date) }}
                                    </button>
                                </td>
                            }
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HlmCalendar<T> {
    public readonly calendarClass = input<ClassValue>('');

    /** Custom weekday format: 'narrow' (S M T W T F S), 'short' (Su Mo Tu...), or 'long' (Sunday Monday...) */
    public readonly weekdayFormat = input<'narrow' | 'short' | 'long'>('short');

    /** Cell size in pixels for date cells */
    public readonly cellSize = input<number>(48);

    /** Font weight for date numbers */
    public readonly dateFontWeight = input<string>('font-semibold');

    /** Button background color for available dates */
    public readonly availableDateBg = input<string>('bg-primary-light');

    /** Button text color for available dates */
    public readonly availableDateText = input<string>('text-primary-dark');

    /** Button background color on hover for available dates */
    public readonly availableDateHoverBg = input<string>('bg-primary-base');

    /** Button text color on hover for available dates */
    public readonly availableDateHoverText = input<string>('text-white');

    /** Button background color for selected date */
    public readonly selectedDateBg = input<string>('bg-primary-base');

    /** Button text color for selected date */
    public readonly selectedDateText = input<string>('text-white');

    protected readonly _computedCalenderClass = computed(() => {
        const size = this.cellSize();
        const customClasses = [
            'w-full',
            'p-lg',
            '[&_.inline-flex]:w-full',
            '[&_.inline-flex]:space-y-2',
            '[&_table]:w-full',
            '[&_thead_tr]:justify-between',
            '[&_tbody_tr]:justify-between',
            '[&_tbody_tr]:mt-0',
            '[&_tbody_tr:not(:first-child)]:mt-md',
            `[&_th]:w-[${size}px]`,
            '[&_th]:h-10',
            '[&_th]:text-base',
            '[&_th]:font-semibold',
            '[&_th]:text-text-primary',
            `[&_td]:w-[${size}px]`,
            `[&_td]:h-[${size}px]`,
            '[&_td]:p-0',
            '[&_button[brnCalendarCellButton]]:w-full',
            '[&_button[brnCalendarCellButton]]:h-full',
            '[&_button[brnCalendarCellButton]]:text-base',
            `[&_button[brnCalendarCellButton]]:${this.dateFontWeight()}`,
            '[&_button[brnCalendarCellButton]]:rounded-md',
            `[&_button[brnCalendarCellButton]:not([data-disabled])]:${this.availableDateBg()}`,
            `[&_button[brnCalendarCellButton]:not([data-disabled])]:${this.availableDateText()}`,
            `[&_button[brnCalendarCellButton]:not([data-disabled]):hover]:${this.availableDateHoverBg()}`,
            `[&_button[brnCalendarCellButton]:not([data-disabled]):hover]:${this.availableDateHoverText()}`,
            `[&_button[brnCalendarCellButton][data-selected]]:${this.selectedDateBg()}`,
            `[&_button[brnCalendarCellButton][data-selected]]:${this.selectedDateText()}`,
            '[&_button[brnCalendarCellButton][data-selected]]:rounded-full',
            '[&_button[brnCalendarCellButton][data-disabled]]:bg-transparent',
            '[&_button[brnCalendarCellButton][data-disabled]]:opacity-40',
            '[&_button[brnCalendarCellButton][data-disabled]]:relative',
            "[&_button[brnCalendarCellButton][data-disabled]]:after:content-['']",
            '[&_button[brnCalendarCellButton][data-disabled]]:after:absolute',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:top-1/2',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:left-1/2',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:-translate-x-1/2',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:-translate-y-1/2',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:w-[1ch]',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:h-[2px]',
            '[&_button[brnCalendarCellButton][data-disabled]]:after:bg-text-disabled',
            // Today's date indicator (dot)
            '[&_button[brnCalendarCellButton][data-today]]:relative',
            "[&_button[brnCalendarCellButton][data-today]]:before:content-['']",
            '[&_button[brnCalendarCellButton][data-today]]:before:absolute',
            '[&_button[brnCalendarCellButton][data-today]]:before:bottom-[6px]',
            '[&_button[brnCalendarCellButton][data-today]]:before:left-1/2',
            '[&_button[brnCalendarCellButton][data-today]]:before:-translate-x-1/2',
            '[&_button[brnCalendarCellButton][data-today]]:before:w-[6px]',
            '[&_button[brnCalendarCellButton][data-today]]:before:h-[6px]',
            '[&_button[brnCalendarCellButton][data-today]]:before:rounded-full',
            '[&_button[brnCalendarCellButton][data-today]]:before:bg-black',
            '[&_button[brnCalendarCellButton][data-today]]:before:z-10',
        ];

        return hlm('rounded-md border p-3', customClasses.join(' '), this.calendarClass());
    });

    /** Access the calendar i18n */
    protected readonly _i18n = injectBrnCalendarI18n();

    /** Access the date time adapter */
    protected readonly _dateAdapter = injectDateAdapter<T>();

    /** The minimum date that can be selected.*/
    public readonly min = input<T>();

    /** The maximum date that can be selected. */
    public readonly max = input<T>();

    /** Show dropdowns to navigate between months or years. */
    public readonly captionLayout = input<'dropdown' | 'label' | 'dropdown-months' | 'dropdown-years'>('label');

    /** Determine if the date picker is disabled. */
    public readonly disabled = input<boolean, BooleanInput>(false, {
        transform: booleanAttribute,
    });

    /** The selected value. */
    public readonly date = model<T>();

    /** Whether a specific date is disabled. */
    public readonly dateDisabled = input<(date: T) => boolean>(() => false);

    /** The day the week starts on */
    public readonly weekStartsOn = input<Weekday, NumberInput>(undefined, {
        transform: (v: unknown) => numberAttribute(v) as Weekday,
    });

    /** The default focused date. */
    public readonly defaultFocusedDate = input<T>();

    /** Access the calendar directive */
    private readonly _calendar = viewChild.required(BrnCalendar);

    /** Check if previous month button should be disabled based on min date */
    protected readonly _isPreviousDisabled = computed(() => {
        const minDate = this.min();
        if (!minDate) return false;

        const focusedDate = this._calendar().focusedDate();
        const currentMonth = this._dateAdapter.getMonth(focusedDate);
        const currentYear = this._dateAdapter.getYear(focusedDate);
        const minMonth = this._dateAdapter.getMonth(minDate);
        const minYear = this._dateAdapter.getYear(minDate);

        return currentYear < minYear || (currentYear === minYear && currentMonth <= minMonth);
    });

    /** Check if next month button should be disabled based on max date */
    protected readonly _isNextDisabled = computed(() => {
        const maxDate = this.max();
        if (!maxDate) return false;

        const focusedDate = this._calendar().focusedDate();
        const currentMonth = this._dateAdapter.getMonth(focusedDate);
        const currentYear = this._dateAdapter.getYear(focusedDate);
        const maxMonth = this._dateAdapter.getMonth(maxDate);
        const maxYear = this._dateAdapter.getYear(maxDate);

        return currentYear > maxYear || (currentYear === maxYear && currentMonth >= maxMonth);
    });

    /** Get the heading for the current month and year */
    protected readonly _heading = computed(() => {
        const config = this._i18n.config();
        const date = this._calendar().focusedDate();

        return {
            header: config.formatHeader(this._dateAdapter.getMonth(date), this._dateAdapter.getYear(date)),
            month: config.formatMonth(this._dateAdapter.getMonth(date)),
            year: config.formatYear(this._dateAdapter.getYear(date)),
        };
    });

    /** Format weekday name based on weekdayFormat input */
    protected _formatWeekday(weekday: number): string {
        const fullName = this._i18n.config().formatWeekdayName(weekday as Weekday);
        const format = this.weekdayFormat();

        if (format === 'narrow') {
            return fullName.charAt(0).toUpperCase();
        } else if (format === 'long') {
            // Map short names to full names
            const longNames: Record<string, string> = {
                Su: 'Sunday',
                Mo: 'Monday',
                Tu: 'Tuesday',
                We: 'Wednesday',
                Th: 'Thursday',
                Fr: 'Friday',
                Sa: 'Saturday',
            };
            return longNames[fullName] || fullName;
        }
        return fullName; // 'short' format (default)
    }

    /** Format month and year as short month name + year */
    protected _formatMonthYear(): string {
        const date = this._calendar().focusedDate();
        const monthIndex = this._dateAdapter.getMonth(date);
        const year = this._dateAdapter.getYear(date);

        // Short month names
        const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        return `${shortMonths[monthIndex]} ${year}`;
    }

    protected readonly _btnClass = hlm(
        buttonVariants({ variant: 'ghost' }),
        'size-8 p-0 font-normal aria-selected:opacity-100',
        'data-[outside]:text-muted-foreground data-[outside]:aria-selected:bg-accent/50 data-[outside]:aria-selected:text-muted-foreground data-[outside]:opacity-50 data-[outside]:aria-selected:opacity-30',
        'data-[today]:bg-accent data-[today]:text-accent-foreground',
        'data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[selected]:hover:bg-primary dark:hover:text-accent-foreground data-[selected]:focus:bg-primary data-[selected]:focus:text-primary-foreground',
        'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50'
    );

    protected readonly _selectClass = 'gap-0 px-1.5 py-2 [&>ng-icon]:ml-1';
}
